import React from "react";
import { Modal, Button, FormSelect, FormInput, SearchableSelect } from "@/components/ui";
import { Plus, Trash2, User, ArrowRight } from "lucide-react";
import AllocationStats from "./AllocationStats";
import { useAllocationData } from "../hooks/useAllocationData";

/**
 * AllocationModal
 * Reusable Batch Contacts Allocation modal.
 *
 * Props:
 *   isOpen             — boolean, controls visibility
 *   onClose            — fn, closes the modal
 *   onSuccess          — fn, callback when allocation is successful (e.g. to refresh main list)
 */
const AllocationModal = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const {
    unassignedCityStats,
    telecallers,
    allocations,
    setAllocations,
    officeLocations,
    loading: dataLoading,
    handleFinalAssign,
  } = useAllocationData({ onSuccess });

  const allocated = allocations.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0);
  const remaining = unassignedCityStats.total - allocated;
  const isOverAllocated = remaining < 0;
  const hasInvalidRow = allocations.some(a => !a.userId || !a.count || parseInt(a.count) <= 0);
  const isConfirmDisabled = allocated <= 0 || isOverAllocated || hasInvalidRow || dataLoading;

  const updateAlloc = (index, patch) => {
    setAllocations(allocations.map((a, i) => i === index ? { ...a, ...patch } : a));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Batch Contacts Allocation"
      size="max-w-6xl"
    >
      <div className="space-y-6">
        {/* Stats Summary */}
        <AllocationStats
          total={unassignedCityStats.total}
          allocated={allocated}
        />

        {/* Allocation Rows */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1 border-b border-slate-100 pb-2">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest pl-1">Assigned To</h4>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setAllocations([...allocations, { userId: "", count: "", location: "" }])}
              disabled={remaining <= 0}
              leftIcon={<Plus size={16} />}
            >
              Add Next Row
            </Button>
          </div>

          <div className="space-y-3">
            {allocations.map((alloc, index) => {
              const maxForCity = alloc.location
                ? (unassignedCityStats.cities.find(c => c.name === alloc.location)?.count || 0)
                : unassignedCityStats.total;
              const isCountOver = parseInt(alloc.count) > maxForCity;

              return (
                <div key={index} className="flex flex-col lg:grid lg:grid-cols-12 items-stretch lg:items-start gap-3 p-4 bg-white rounded-md border border-slate-100 shadow-sm animate-in fade-in slide-in-from-top-2 transition-all">
                  {/* Location Filter */}
                  <div className="lg:col-span-3">
                    <SearchableSelect
                      label="Location Filter"
                      value={alloc.location}
                      onChange={val => updateAlloc(index, { location: val })}
                      placeholder={`All Cities (${unassignedCityStats.cities.length})`}
                      options={[
                        { value: "", label: `All Cities (${unassignedCityStats.cities.length})` },
                        ...unassignedCityStats.cities.map(cityObj => ({
                          value: cityObj.name,
                          label: `${cityObj.name} (${cityObj.count})`
                        }))
                      ]}
                    />
                  </div>

                  {/* Team Member */}
                  <div className="lg:col-span-6">
                    <SearchableSelect
                      label="Select Team Member"
                      value={alloc.userId}
                      onChange={val => updateAlloc(index, { userId: val })}
                      placeholder="-- Select Member --"
                      options={[
                        { value: "", label: "-- Select Member --" },
                        ...(Array.isArray(telecallers) ? telecallers : [])
                          .filter(m => {
                            const isCurrent = String(m._id) === String(alloc.userId);
                            const isUsedElsewhere = allocations.some((a, i) => i !== index && String(a.userId) === String(m._id));
                            const isActive = m.active !== false && String(m.status || "Active").toLowerCase() !== 'inactive';
                            return (isCurrent || (!isUsedElsewhere && isActive));
                          })
                          .map(m => ({ value: m._id, label: m.name || m.username }))
                      ]}
                    />
                  </div>

                  {/* Count */}
                  <div className="lg:col-span-2">
                    <FormInput
                      type="number"
                      min="0"
                      max={maxForCity}
                      placeholder="0"
                      label={
                        <div className="flex justify-between items-center w-full min-w-[90px]">
                          <span>Count</span>
                          <span className="text-[10px] text-blue-600 font-bold ml-2">Max: {maxForCity}</span>
                        </div>
                      }
                      inputClassName={`font-bold text-center ${isCountOver ? '!text-red-600' : ''}`}
                      error={isCountOver ? "Exceeds max" : null}
                      value={alloc.count}
                      onChange={e => updateAlloc(index, { count: e.target.value })}
                    />
                  </div>

                  {/* Remove row */}
                  <div className="lg:col-span-1 pt-7 flex justify-end">
                    {allocations.length > 1 && (
                      <Button
                        variant="ghost-danger"
                        size="icon"
                        onClick={() => setAllocations(allocations.filter((_, i) => i !== index))}
                      >
                        <Trash2 size={18} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-slate-100">
          <Button variant="secondary" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button
            variant="orange"
            disabled={isConfirmDisabled}
            onClick={handleFinalAssign}
            rightIcon={<ArrowRight size={18} />}
            fullWidth
          >
            Confirm Allocation
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AllocationModal;

